import AttributeValues from "./AttributeValues";
import {
  getRecordsToOverride,
  ActionLabels,
  ActionDisplayLabels,
  AttributeLabels,
  AttributeDisplayLabels,
  getIsEmptyAttribute,
} from "./Utils";

/**
 * Returns a string indicating the number of records that will be overridden
 *
 * @param {Object} formValues - The form values containing selected records and override details
 * @returns {string} - A string indicating the number of records to be overridden
 */
export const RecordOverrideCountString = (formValues) => {
  const count = getRecordsToOverride(
    formValues.selectedRecords,
    formValues.attribute,
    formValues.system,
    formValues.attrCode,
    formValues.action,
    formValues.incorrectValue,
    formValues.code,
  ).length;

  return count > 0 ? "[" + count + "]" + " record(s) will be overridden." : "";
};

/**
 * Returns a list of attribute dropdown values based on the specified action
 *
 * @param {string} action - The action to determine which attributes to include
 * @returns {Array} - A list of attribute dropdown values
 */
export const AttributeDropDownValues = (action) => {
  const attributes = [
    { value: AttributeLabels.NAME, displayName: AttributeDisplayLabels.NAME },
    {
      value: AttributeLabels.IDENTIFIER,
      displayName: AttributeDisplayLabels.IDENTIFIER,
    },
    {
      value: AttributeLabels.ADDRESS,
      displayName: AttributeDisplayLabels.ADDRESS,
    },
    {
      value: AttributeLabels.GENDER,
      displayName: AttributeDisplayLabels.GENDER,
    },
    {
      value: AttributeLabels.BIRTHDATE,
      displayName: AttributeDisplayLabels.BIRTHDATE,
    },
    { value: AttributeLabels.PHONE, displayName: AttributeDisplayLabels.PHONE },
    { value: AttributeLabels.EMAIL, displayName: AttributeDisplayLabels.EMAIL },
  ].sort((a, b) => a.displayName.localeCompare(b.displayName));

  // birthDate and gender cannot be inactivated or invalidated.
  if ([ActionLabels.INVALID, ActionLabels.INACTIVE].includes(action)) {
    return attributes.filter(
      (v) =>
        v.value !== AttributeLabels.BIRTHDATE &&
        v.value !== AttributeLabels.GENDER,
    );
  }
  return attributes;
};

/**
 * A list of action dropdown values sorted by display name.
 */
export const ActionDropDownValues = [
  { value: ActionLabels.INVALID, displayName: ActionDisplayLabels.INVALID },
  { value: ActionLabels.INACTIVE, displayName: ActionDisplayLabels.INACTIVE },
  { value: ActionLabels.REPLACE, displayName: ActionDisplayLabels.REPLACE },
  { value: ActionLabels.RESTORE, displayName: ActionDisplayLabels.RESTORE },
].sort((a, b) => a.displayName.localeCompare(b.displayName));

/**
 * Returns a list of identifier system dropdown values from the provided map array
 *
 * @param {Array} mapArray - An array of maps containing identifier information
 * @returns {Array} - A list of unique identifier system dropdown values
 */
export const IdentifierSystemDropDownValues = (mapArray) => {
  return [
    ...new Set(
      mapArray.flat().map((v) => {
        if (v.has("system")) {
          return v.get("system");
        } else {
          return "[No System]";
        }
      }),
    ),
  ].sort();
};

/**
 * Returns a list of identifier code dropdown values from the provided map array
 *
 * @param {Array} valueMapList - An array of maps containing identifier information
 * @returns {Array} - A list of unique identifier code dropdown values
 */
export const IdentifierCodeDropDownValues = (valueMapList) => {
  return [
    ...new Set(
      valueMapList
        .flat()
        .filter((v) => !v.has("system") && v.has("code"))
        .map((v) => v.get("code")),
    ),
  ].sort();
};

/**
 * Returns a list of attribute code dropdown values from the provided map array
 *
 * @param {Array} valueMapList - An array of maps containing attribute information
 * @returns {Array} - A list of unique attribute code dropdown values
 */
export const AttrCodeDropDownValues = (valueMapList) => {
  return [
    ...new Set(
      valueMapList
        .flat()
        .filter((v) => v?.has("attrCode"))
        .map((v) => v.get("attrCode")),
    ),
  ].sort();
};

/**
 * Returns a list of attribute value options based on the specified attribute and filters
 *
 * @param {Array} attributeValues - An array of maps containing attribute information
 * @param {string} attribute - The attribute to filter by
 * @param {string} system - The system to filter by (for identifiers)
 * @param {string} attrCode - The attribute code to filter by (for certain attributes)
 * @param {string} code - The code to filter by (for identifiers)
 * @returns {Array} - A list of unique attribute value options
 */
export const AttributeValueOptions = (
  attributeValues,
  attribute,
  system,
  attrCode,
  code,
) => {
  var attributeMaps = attributeValues.flat();

  switch (attribute) {
    case AttributeLabels.BIRTHDATE:
    case AttributeLabels.GENDER:
      return [
        ...new Set(attributeMaps.filter((v) => v.has("displayValue"))).map(
          (v) => v.get("displayValue"),
        ),
      ];
      break;

    case AttributeLabels.IDENTIFIER:
      if (code != "" && system === "[No System]") {
        return [
          ...new Set(
            attributeMaps
              .filter(
                (v) =>
                  v.has("displayValue") &&
                  v.has("code") &&
                  v.get("code") === code &&
                  !v.has("system"),
              )
              .map((v) => v.get("displayValue")),
          ),
        ];
      } else {
        return [
          ...new Set(
            attributeMaps
              .filter(
                (v) =>
                  v.has("displayValue") &&
                  v.has("system") &&
                  v.get("system") === system,
              )
              .map((v) => v.get("displayValue")),
          ),
        ];
      }
      break;

    case AttributeLabels.ADDRESS:
    case AttributeLabels.NAME:
    case AttributeLabels.PHONE:
    case AttributeLabels.EMAIL:
      return [
        ...new Set(
          attributeMaps
            .filter(
              (v) =>
                v?.has("displayValue") &&
                v?.has("attrCode") &&
                v.get("attrCode") === attrCode,
            )
            .map((v) => v.get("displayValue")),
        ),
      ];
      break;
  }

  return attributeMaps;
};

/**
 * Returns a list of proposed attribute value options based on the specified attribute and filters
 *
 * @param {Array} attributeValues - An array of maps containing attribute information
 * @param {string} attribute - The attribute to filter by
 * @param {string} system - The system to filter by (for identifiers)
 * @param {string} attrCode - The attribute code to filter by (for certain attributes)
 * @param {string} code - The code to filter by (for identifiers)
 * @returns {Array} - A list of unique proposed attribute value options
 */
export const ProposedValueOptions = (
  attributeValues,
  attribute,
  system,
  attrCode,
  code,
) => {
  var attributeMaps = attributeValues.flat();

  switch (attribute) {
    case AttributeLabels.BIRTHDATE:
    case AttributeLabels.GENDER:
      return [
        ...new Set(attributeMaps.filter((v) => v.has("displayValue"))).map(
          (v) => v.get("displayValue"),
        ),
      ];
      break;

    case AttributeLabels.IDENTIFIER:
      if (code != "" && system === "[No System]") {
        return [
          ...new Set(
            attributeMaps
              .filter(
                (v) =>
                  v.has("displayValue") &&
                  v.get("use")?.toUpperCase() !== "OLD" &&
                  v.has("code") &&
                  v.get("code") === code &&
                  !v.has("system"),
              )
              .map((v) => v.get("displayValue")),
          ),
        ];
      } else {
        return [
          ...new Set(
            attributeMaps
              .filter(
                (v) =>
                  v.has("displayValue") &&
                  v.get("use")?.toUpperCase() !== "OLD" &&
                  v.has("system") &&
                  v.get("system") === system,
              )
              .map((v) => v.get("displayValue")),
          ),
        ];
      }
      break;

    case AttributeLabels.ADDRESS:
    case AttributeLabels.NAME:
    case AttributeLabels.PHONE:
    case AttributeLabels.EMAIL:
      return [
        ...new Set(
          attributeMaps
            .filter(
              (v) =>
                !getIsEmptyAttribute(attribute, v) &&
                v?.has("displayValue") &&
                v.get("use")?.toUpperCase() !== "OLD" &&
                v?.has("attrCode") &&
                v.get("attrCode") === attrCode,
            )
            .map((v) => v.get("displayValue")),
        ),
      ];
      break;
  }

  return attributeMaps;
};
